import { Button } from "@/src/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTrigger } from "@/src/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/src/components/ui/form";
import { Input } from "@/src/components/ui/input";
import { InputNumber } from "@/src/components/ui/input-number";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/ui/select";
import { useAsyncList } from "@/src/lib/useAsyncList";
import { zodResolver } from "@hookform/resolvers/zod";
import { Separator } from "@radix-ui/react-separator";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { useEmit } from "@/src/lib/actions";
import { useEffect, useState } from "react";
import { Operator as OperatorComboBox } from "./filters/Operator";
import { Customer as CustomerComboBox } from "./filters/Customer";

const schema = z.object({
  customer: z
    .object({ id: z.number(), firstName: z.string(), lastName: z.string(), email: z.string() })
    .optional()
    .nullable(),
  operator: z.object({ id: z.string(), name: z.string() }).optional().nullable(),
  amountFrom: z.number().optional(),
  amountTo: z.number().optional(),
  currency: z.string().optional(),
  bookingRef: z.string().optional(),
  operatorRef: z.string().optional(),
});
export type FiltersInput = z.infer<typeof schema>;

export function FiltersButton({ activeTab }: { activeTab: "customers" | "operators" }) {
  const [open, setOpen] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const currencyCodes = useAsyncList<string>("/api/currencies/codes");

  const form = useForm<FiltersInput>({
    resolver: zodResolver(schema),
    defaultValues: {
      customer: undefined,
      operator: undefined,
      amountFrom: undefined,
      amountTo: undefined,
      currency: undefined,
      bookingRef: undefined,
      operatorRef: undefined,
    },
  });

  const operatorValue = form.watch("operator");
  const customerValue = form.watch("customer");

  const emitCustomerId = useEmit<number | undefined>("payments:filter-customer-id");
  const emitOperatorId = useEmit<string | undefined>("payments:filter-operator-id");
  const emitAmountFrom = useEmit<number | null>("payments:filter-amount-from");
  const emitAmountTo = useEmit<number | null>("payments:filter-amount-to");
  const emitCurrency = useEmit<string | null>("payments:filter-amount-currency");
  const emitBookingRef = useEmit<string | null>("payments:filter-booking-ref");
  const emitOperatorRef = useEmit<string | null>("payments:filter-operator-ref");

  const onSubmit: SubmitHandler<FiltersInput> = async (data) => {
    try {
      const customerId = data?.customer?.id;
      const operatorId = data?.operator?.id;
      const amountFrom = data.amountFrom;
      const amountTo = data.amountTo;
      const currency = data.currency;
      const bookingRef = data.bookingRef;
      const operatorRef = data.operatorRef;

      if (customerId || customerId === undefined) emitCustomerId(customerId);
      if (operatorId || operatorId === undefined) emitOperatorId(operatorId);
      if (amountFrom || amountFrom === 0) emitAmountFrom(amountFrom);
      if (amountTo || amountTo === 0) emitAmountTo(amountTo);
      if (currency) emitCurrency(currency);
      if (bookingRef || bookingRef === "") emitBookingRef(bookingRef);
      if (operatorRef || operatorRef === "") emitOperatorRef(operatorRef);

      setOpen(false);
    } catch (error) {
      console.error("Submission error:", error);
    }
  };

  const resetForm = () => {
    emitCustomerId(undefined);
    emitOperatorId(undefined);
    emitAmountFrom(null);
    emitAmountTo(null);
    emitCurrency(null);
    emitBookingRef(null);
    emitOperatorRef(null);
    form.reset();
    setResetKey((prevKey) => prevKey + 1);
  };

  const onClearAll = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    resetForm();
  };

  useEffect(() => {
    resetForm();
  }, [activeTab]);

  return (
    <Dialog open={open} onOpenChange={(open) => setOpen(open)}>
      <DialogTrigger asChild>
        <Button variant="action">Filters</Button>
      </DialogTrigger>
      <DialogContent
        className="max-w-xl"
        style={{
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <DialogHeader>
          <h2 className="text-3xl">Filters</h2>
        </DialogHeader>
        <Separator />
        <Form {...form}>
          <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)} key={resetKey}>
            <div className="flex items-start gap-4">
              <CustomerComboBox defaultValue={customerValue} />
              <div className="flex-grow w-1/2"></div>
            </div>

            <div className="flex items-start gap-4">
              <OperatorComboBox defaultValue={operatorValue} />
              <div className="flex-grow w-1/2"></div>
            </div>

            <div>
              <h6 className="text-xl font-semibold mb-2">Step amount</h6>
              <div className="flex items-start gap-4 mb-2">
                <FormField<FiltersInput, "amountFrom">
                  name="amountFrom"
                  render={({ field }) => (
                    <FormItem className="flex-grow">
                      <FormLabel>From</FormLabel>
                      <FormControl>
                        <InputNumber placeholder="Enter amount..." {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField<FiltersInput, "amountTo">
                  name="amountTo"
                  render={({ field }) => (
                    <FormItem className="flex-grow">
                      <FormLabel>To</FormLabel>
                      <FormControl>
                        <InputNumber placeholder="Enter amount..." {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex items-start gap-4">
                <FormField<FiltersInput, "currency">
                  name="currency"
                  render={({ field: { value, onChange } }) => (
                    <FormItem className="flex-grow w-1/2">
                      <FormLabel>Currency</FormLabel>
                      <Select onValueChange={onChange} defaultValue={value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {currencyCodes.map((code) => (
                            <SelectItem key={code} value={code}>
                              {code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
                <div className="flex-grow w-1/2"></div>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <FormField<FiltersInput, "bookingRef">
                name="bookingRef"
                render={({ field }) => (
                  <FormItem className="flex-grow w-1/2">
                    <FormLabel className="text-xl font-semibold">Booking reference number</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter reference number..." {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="flex-grow w-1/2"></div>
            </div>

            <div className="flex items-start gap-4">
              <FormField<FiltersInput, "operatorRef">
                name="operatorRef"
                render={({ field }) => (
                  <FormItem className="flex-grow w-1/2">
                    <FormLabel className="text-xl font-semibold">Operator reference number</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter reference number..." {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="flex-grow w-1/2"></div>
            </div>

            <div className="mb-2 flex justify-start items-center gap-2">
              <Button className="mr-auto" variant="link" onClick={(e) => onClearAll(e)}>
                Clear all
              </Button>
              <Button type="submit">Show payments</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
